/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Data formatting and transformation functions for API responses
 */

import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

// Interfaces for API response items
interface BgpNeighborItem {
  neighborAddress?: string;
  bgpNeighbor?: {
    neighborAddress?: string;
    peerAs?: number;
    operatingMode?: string;
    updatedAt?: string;
    id?: number;
    reflectorInstanceId?: number;
  };
  reflectorInstanceId?: number;
}

interface InterfaceItem {
  interface?: string;
  name?: string;
  ipAddress?: string;
  ip?: string;
  macAddress?: string;
  mac?: string;
  vpcId?: string;
  cloudSubnetId?: string;
  interfaceAddresses?: Array<{ ipAddress: string }>;
  interfaceAddress?: { ipAddress: string } | string; // Cluster mode: can be object with ipAddress or string
}

interface SubnetItem {
  subnet_formatted: string;
}

// Cloud Propagation Data Transformers
export const transformCloudPropagationData = (apiResponse: unknown): any[] => {
  const response = apiResponse as { data?: BgpNeighborItem[] } | BgpNeighborItem[];
  
  // Handle new API structure - wrapped in data property (current structure)
  if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
    return response.data.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  // Handle direct array response (fallback)
  if (Array.isArray(response)) {
    return response.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  return [];
};

// Cluster Cloud Propagation Data Transformer
export const transformClusterCloudPropagationData = (apiResponse: any): any[] => {
  // Handle new API structure - wrapped in data property (current structure)
  if (apiResponse?.data && Array.isArray(apiResponse.data)) {
    return apiResponse.data.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  // Handle direct array response (fallback)
  if (Array.isArray(apiResponse)) {
    return apiResponse.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  return [];
};

// Interface Mapping Functions
export const mapInterfaces = (interfaces: InterfaceItem[] = []): any[] => {
  return interfaces.map(iface => {
    // Handle IP address from interfaceAddresses array (preferred - same as standalone mode)
    let ipAddress = '-';
    if (Array.isArray(iface.interfaceAddresses) && iface.interfaceAddresses.length > 0) {
      // Get IP addresses from the array, or join multiple IPs
      const ips = iface.interfaceAddresses
        .map((addr: any) => addr.ipAddress)
        .filter((ip: string) => ip !== null && ip !== undefined && ip !== 'none' && ip !== '');
      ipAddress = ips.length > 0 ? ips.join(', ') : '-';
    } else if (iface.interfaceAddress) {
      // Handle interfaceAddress (cluster mode - can be object with ipAddress or string)
      if (typeof iface.interfaceAddress === 'object' && iface.interfaceAddress !== null) {
        ipAddress = iface.interfaceAddress.ipAddress || '-';
      } else if (typeof iface.interfaceAddress === 'string') {
        ipAddress = iface.interfaceAddress || '-';
      }
    } else if (iface.ipAddress) {
      ipAddress = iface.ipAddress;
    } else if (iface.ip) {
      ipAddress = iface.ip;
    }
    
    return {
      name: iface.interface || iface.name || '-',
      ip: ipAddress,
      mac: iface.macAddress || iface.mac || '-',
    };
  });
};

// Enhanced interface mapping for reflector details
export const mapReflectorInterfaces = (interfaces: InterfaceItem[] = []): any[] => {
  return interfaces.map((iface: InterfaceItem) => ({
    ...iface,
    ipAddress: Array.isArray(iface.interfaceAddresses)
      ? iface.interfaceAddresses
          .map((addr: any) => addr.ipAddress)
          .filter((ip: string) => ip !== null && ip !== undefined && ip !== 'none')
          .join(', ')
      : '',
  }));
};

// Subnet Data Transformers
export const extractSubnets = (apiResponse: any): string[] => {
  if (apiResponse?.data?.data?.subnets) {
    return apiResponse.data.data.subnets.map((subnet: SubnetItem) => subnet.subnet_formatted);
  }
  return [];
};

/** Maps cloud-elements/tag-keys API response to searchable dropdown items. */
export const extractTagKeyOptions = (
  apiResponse: { data?: { data?: { tagKeys?: unknown; tag_keys?: unknown } | unknown[] } }
): Array<{ value: string; label: string }> => {
  const payload = apiResponse?.data?.data;
  const rawKeys = Array.isArray(payload)
    ? payload
    : (payload as { tagKeys?: unknown; tag_keys?: unknown })?.tagKeys ??
      (payload as { tagKeys?: unknown; tag_keys?: unknown })?.tag_keys;

  if (!Array.isArray(rawKeys)) return [];

  return rawKeys
    .filter((key: unknown) => typeof key === "string" && key.trim())
    .map((key: string) => ({ value: key.trim(), label: key.trim() }));
};

/** @deprecated Use extractTagKeyOptions */
export const extractTagNameOptions = extractTagKeyOptions;

// Route Tables and Network Interfaces Data Transformers
// Return objects with value (elementId) and label (displayLabel or elementId as fallback)
export const extractRouteTables = (apiResponse: any): Array<{ value: string; label: string }> => {
  if (apiResponse?.data?.data?.route_tables) {
    return apiResponse.data.data.route_tables
      .map((item: any) => ({
        value: item.elementId,
        label: item.displayLabel || item.elementId
      }))
      .filter((item: any) => item.value != null && item.value !== '');
  }
  return [];
};

export const extractNetworkInterfaces = (apiResponse: any): Array<{ value: string; label: string }> => {
  if (apiResponse?.data?.data?.network_interfaces) {
    return apiResponse.data.data.network_interfaces
      .map((item: any) => {
        const baseLabel = item.displayLabel || item.elementId;
        const description = item.description || '';
        const privateIpAddress = item.private_ip_address || '';
        
        // Build label with description and private_ip_address in parentheses
        let label = baseLabel;
        const additionalInfo: string[] = [];
        
        if (description) {
          additionalInfo.push(description);
        }
        if (privateIpAddress) {
          additionalInfo.push(privateIpAddress);
        }
        
        if (additionalInfo.length > 0) {
          label = `${baseLabel} (${additionalInfo.join(', ')})`;
        }
        
        return {
          value: item.elementId,
          label: label
        };
      })
      .filter((item: any) => item.value != null && item.value !== '');
  }
  return [];
};

export const extractVpcs = (apiResponse: any): Array<{ value: string; label: string }> => {
  const vpcs = apiResponse?.data?.data?.vpcs;
  if (!vpcs) return [];

  const vpcLabelsMap = new Map<string, string>();
  const vpcLabels = apiResponse?.data?.data?.vpc_labels;
  if (Array.isArray(vpcLabels)) {
    vpcLabels.forEach((item: any) => {
      if (item?.vpc_id && item?.vpc_label) {
        vpcLabelsMap.set(item.vpc_id, item.vpc_label);
      }
    });
  }

  return vpcs
    .filter((vpc: any) => vpc != null && vpc !== '')
    .map((vpc: string) => ({
      value: vpc,
      label: vpcLabelsMap.get(vpc) || vpc
    }));
};

// Full API Data Extractors (for filtering)
export const extractFullRouteTablesData = (apiResponse: any): any[] => {
  return apiResponse?.data?.data?.route_tables || [];
};

export const extractFullNetworkInterfacesData = (apiResponse: any): any[] => {
  return apiResponse?.data?.data?.network_interfaces || [];
};

// Transit Gateways Extractors
export const extractTransitGateways = (apiResponse: any): Array<{ value: string; label: string }> => {
  if (apiResponse?.data?.data?.transit_gateways) {
    return apiResponse.data.data.transit_gateways
      .map((item: any) => ({
        value: item.elementId,
        label: item.displayLabel || item.elementId
      }))
      .filter((item: any) => item.value != null && item.value !== '');
  }
  return [];
};

export const extractFullTransitGatewaysData = (apiResponse: any): any[] => {
  return apiResponse?.data?.data?.transit_gateways || [];
};

// Status Mapping Functions
export const mapAvailabilityToStatus = (available: boolean | number | null): string => {
  // Handle boolean (centralized usage)
  if (typeof available === 'boolean') {
    return available ? 'Available' : 'Unavailable';
  }
  
  // Handle number/null (legacy usage)
  if (available === 1) {
    return "Online";
  } else if (available === 0 || available === null) {
    return "Offline";
  }
  return "Offline"; // Default case
};

export const getStatusVariant = (status?: string): 'success' | 'warning' | 'error' | 'neutral' | 'default' => {
  if (!status) return 'neutral';
  
  switch (status.toLowerCase()) {
    // Centralized status values
    case 'available':
      return 'success';
    case 'unavailable':
      return 'error';
    case 'pending':
      return 'warning';
    // Legacy status values
    case 'online':
      return 'success';
    case 'offline':
      return 'error';
    case 'degraded':
      return 'warning';
    default:
      return 'neutral';
  }
};

// Date Formatting Functions
export const formatDateTimeUTC = (dateString?: string, useLegacyFormat = false): string => {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    
    if (useLegacyFormat) {
      // Use manual formatting for legacy compatibility (dd/MM/yyyy HH:mm:ss)
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } else {
      // Use toLocaleString for new format
      return date.toLocaleString();
    }
  } catch (error) {
    return "-";
  }
};

/**
 * formatCiscoUptime(epoch)
 * - epoch: number (seconds or milliseconds) or Date object
 * - returns: string with "Cisco" style format showing up to 2 consecutive units
 *
 * Examples:
 *  "2 years, 3 weeks"
 *  "4 days, 6 hours"
 *  "12 minutes"
 *  "45 seconds"
 *  "0 minutes" (if timestamp equals now)
 */
export const formatCiscoUptime = (epoch: number | Date): string => {
  // Normalize input to milliseconds
  let ts: number;
  if (epoch instanceof Date) {
    ts = epoch.getTime();
  } else if (typeof epoch === "number") {
    // Detect if seconds (<= 1e12 -> seconds) or ms (> 1e12)
    ts = epoch > 1e12 ? epoch : epoch * 1000;
  } else {
    throw new TypeError("epoch must be a number (seconds or ms) or a Date");
  }

  const now = Date.now();
  let diffMs = now - ts;

  if (diffMs < 0) {
    return "0 minutes (timestamp is in the future)";
  }

  // Units in ms
  const MS = {
    year: 365 * 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
  };

  const parts: { [key: string]: number } = {};
  parts.years = Math.floor(diffMs / MS.year);
  diffMs -= parts.years * MS.year;
  parts.weeks = Math.floor(diffMs / MS.week);
  diffMs -= parts.weeks * MS.week;
  parts.days = Math.floor(diffMs / MS.day);
  diffMs -= parts.days * MS.day;
  parts.hours = Math.floor(diffMs / MS.hour);
  diffMs -= parts.hours * MS.hour;
  parts.minutes = Math.floor(diffMs / MS.minute);
  diffMs -= parts.minutes * MS.minute;
  parts.seconds = Math.floor(diffMs / MS.second);

  // Order of units from largest to smallest
  const ordered: Array<[string, string, number]> = [
    ["years", "year", parts.years],
    ["weeks", "week", parts.weeks],
    ["days", "day", parts.days],
    ["hours", "hour", parts.hours],
    ["minutes", "minute", parts.minutes],
    ["seconds", "second", parts.seconds],
  ];

  // Find first non-zero unit and next consecutive non-zero unit
  const resultParts: string[] = [];

  // Find index of first non-zero unit
  const firstIdx = ordered.findIndex(u => u[2] > 0);

  if (firstIdx === -1) {
    // All zero -> uptime 0 minutes
    return "0 minutes";
  }

  // Helper to format unit
  const fmtUnit = (count: number, singular: string): string => {
    if (count === 1) return `1 ${singular}`;
    // plural regular english form (Cisco-style uses english words)
    return `${count} ${singular}s`;
  };

  resultParts.push(fmtUnit(ordered[firstIdx][2], ordered[firstIdx][1]));

  // Add next consecutive non-zero unit (if exists)
  for (let i = firstIdx + 1; i < ordered.length; i++) {
    if (ordered[i][2] > 0) {
      resultParts.push(fmtUnit(ordered[i][2], ordered[i][1]));
      break;
    }
  }

  return resultParts.join(", ");
};

// Tag Processing Functions
export const processTags = (tags: any): string[] => {
  if (Array.isArray(tags)) {
    return tags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '');
  }
  if (typeof tags === 'string' && tags.trim() !== '') {
    // Handle legacy string format - split by comma
    return tags.split(',').map(t => t.trim()).filter(t => t !== '');
  }
  return [];
};

// IAM Permissions Data Transformers
// Standalone IAM permissions transformer
export const transformStandaloneIamPermissions = (apiResponse: any): any[] => {
  // Handle new API structure: { reflector: {...}, permissions: [...] }
  if (apiResponse?.data?.permissions && Array.isArray(apiResponse.data.permissions)) {
    return apiResponse.data.permissions;
  }
  
  // Handle direct array response (fallback)
  if (Array.isArray(apiResponse?.data)) {
    return apiResponse.data;
  }
  
  return [];
};

// Cluster IAM permissions transformer
export const transformClusterIamPermissions = (apiResponse: any): any => {
  // Handle cluster API structure: { success: true, data: { reflector1: {...}, reflector2: {...}, permissions: [...] } }
  if (apiResponse?.data?.success && apiResponse.data.data) {
    return apiResponse.data.data;
  }
  
  // Handle direct data structure (fallback)
  if (apiResponse?.data && apiResponse.data.reflector1 && apiResponse.data.reflector2) {
    return apiResponse.data;
  }
  
  return null;
};

// Reflector Data Transformer
export const transformReflectorDetails = (responseData: any): any => {
  const mappedInterfaces = mapReflectorInterfaces(responseData.interfaces || []);
  // Tags exposed by `getReflectorDetails` after KD-563-2 (TAGS-API-CONTRACT.md
  // §5.5): `tags` is the rich `[{id, name, color, category}]` array and
  // `tagsLegacy` is the original string array (renamed to avoid colliding
  // with the new field name). During the deprecation window we expose both:
  //  - `tags` → legacy strings (drives the production `LegacyTagManagementModal`)
  //  - `tagsRich` → rich array (seeds `EditableTagList` picker mode)
  // The fallback `tagsLegacy ?? tags` keeps pre-v2 environments working —
  // `processTags` silently drops non-string entries so a rich array becomes [].
  const tagsArray = processTags(responseData.tagsLegacy ?? responseData.tags);
  const richTags = apiTagsToSummaries(responseData.tags);
  
  // Parse data.interfaces if it exists (may be a string representation)
  let dataInterfaces: any[] = [];
  if (responseData.data?.interfaces) {
    try {
      // If it's a string, parse it as JSON
      if (typeof responseData.data.interfaces === 'string') {
        dataInterfaces = JSON.parse(responseData.data.interfaces);
      } else if (Array.isArray(responseData.data.interfaces)) {
        dataInterfaces = responseData.data.interfaces;
      }
    } catch (error) {
      console.warn('Failed to parse data.interfaces:', error);
    }
  }
  
  // Enrich interfaces with VPC ID and Cloud Subnet ID from data.interfaces if missing
  const enrichedInterfaces = mappedInterfaces.map((iface: any) => {
    // If vpcId or cloudSubnetId are missing, try to find matching interface in data.interfaces
    if ((!iface.vpcId || !iface.cloudSubnetId) && dataInterfaces.length > 0) {
      // Try to match by interface name or MAC address
      const matchedInterface = dataInterfaces.find((dataIface: any) => {
        // Normalize interface names for comparison (remove common prefixes/suffixes)
        const normalizeName = (name: string) => name?.toLowerCase().trim() || '';
        const ifaceName = normalizeName(iface.interface || iface.name || '');
        const dataIfaceName = normalizeName(dataIface.name || dataIface.interface || '');
        
        const nameMatch = ifaceName && dataIfaceName && (
          ifaceName === dataIfaceName ||
          ifaceName.includes(dataIfaceName) ||
          dataIfaceName.includes(ifaceName)
        );
        
        // Normalize MAC addresses for comparison (remove colons, dashes, convert to lowercase)
        const normalizeMac = (mac: string) => mac?.replace(/[:-]/g, '').toLowerCase() || '';
        const ifaceMac = normalizeMac(iface.macAddress || iface.mac || '');
        const dataIfaceMac = normalizeMac(dataIface.mac || dataIface.macAddress || '');
        
        const macMatch = ifaceMac && dataIfaceMac && ifaceMac === dataIfaceMac;
        
        return nameMatch || macMatch;
      });
      
      if (matchedInterface) {
        return {
          ...iface,
          vpcId: iface.vpcId || matchedInterface.vpc_id || matchedInterface.vpcId || matchedInterface.vpc || '',
          cloudSubnetId: iface.cloudSubnetId || matchedInterface.subnet_id || matchedInterface.subnetId || matchedInterface.cloudSubnetId || matchedInterface.subnet || '',
        };
      }
    }
    return iface;
  });
  
  return {
    ...responseData,
    id: String(responseData.id),
    name: responseData.name || '',
    description: responseData.description || '',
    subId: responseData.subId || '',
    status: mapAvailabilityToStatus(responseData.available),
    cloud: responseData.data?.cloud ? responseData.data.cloud.toUpperCase() : '',
    region: responseData.data?.region || '',
    cluster: responseData.cluster?.name || (responseData.clusterId ? String(responseData.clusterId) : '—'),
    clusterId: responseData.cluster?.id ? String(responseData.cluster.id) : responseData.clusterId ? String(responseData.clusterId) : undefined,
    version: responseData.version || '',
    lastSync: responseData.lastUpdate || '-',
    createdAt: responseData.createdAt || '-',
    data: responseData.data,
    cloudElements: responseData.cloudElements || [],
    interfaces: enrichedInterfaces,
    tags: tagsArray,
    tagsRich: richTags,
  };
};
